import type { Task } from "features/types/task";
import { usePitcher } from "features/utils/usePitcher";
import { useEffect, useState } from "react";

export type UseTaskReturnType = {
  task: Task | null;
  killedBy: string | null;
  taskInitializing: boolean;
};

export const useTask = (taskId: string): UseTaskReturnType => {
  const pitcher = usePitcher();

  const [task, setTask] = useState<Task | null>(
    pitcher.clients.task.getTask(taskId) || null,
  );
  const [killedBy, setKilledBy] = useState<string | null>(null);

  // Task is considered initialzing if no shell exists
  // After a 500ms timeout we consider the task killed
  const [taskInitializing, setTaskInitializing] = useState(
    !task || !task.shell,
  );

  useEffect(() => {
    // Wait 500ms before the task is considered killed (no shell running)
    const timeout = setTimeout(() => {
      setTaskInitializing(false);
    }, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    const taskListener = pitcher.clients.task.onTaskUpdate((updatedTask) => {
      if (updatedTask.id === taskId) {
        setTask(updatedTask);
      }
    });

    const taskListListener = pitcher.clients.task.onTaskListUpdate(
      (taskList) => {
        const updatedTask = taskList.tasks[taskId] || null;
        setTask(updatedTask);
      },
    );

    const shellTerminateListener = pitcher.clients.shell.onShellTerminated(
      ({ shellId: terminatedShellId, author }) => {
        if (task?.shell?.shellId === terminatedShellId) {
          setKilledBy(author);
        }
      },
    );

    return () => {
      taskListener.dispose();
      taskListListener.dispose();
      shellTerminateListener.dispose();
    };
  }, [taskId, task?.shell?.shellId]);

  return {
    task,
    killedBy,
    taskInitializing,
  };
};
